import axios from "axios";
import { invalidateSession, isTokenExpired } from "./session";

const normalizeBaseOrigin = (value) => {
  const rawValue = String(value || "http://localhost:3050").trim();
  return rawValue.replace(/\/api\/v1\/?$/i, "").replace(/\/+$/, "");
};

export const API_ORIGIN = normalizeBaseOrigin(process.env.REACT_APP_API_URL);
export const API_BASE_URL = `${API_ORIGIN}/api/v1`;
export const PUBLIC_TOKEN = String(process.env.REACT_APP_TOKEN_PUBLIC || "").trim();
export const SITE_URL = String(process.env.REACT_APP_SITE_URL || "https://lamanchaamarilla.com").trim().replace(/\/+$/, "");

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

let currentAuthScope = null;

const extractBearerToken = (value) => {
  if (!value || typeof value !== "string") return "";
  const match = value.match(/^Bearer\s+(.+)$/i);
  return match ? match[1].trim() : "";
};

export const setAuthToken = (token, scope = null) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    currentAuthScope = scope || currentAuthScope;
  } else {
    delete api.defaults.headers.common.Authorization;
    currentAuthScope = null;
  }
};

export const clearAuthToken = () => {
  delete api.defaults.headers.common.Authorization;
  currentAuthScope = null;
};

api.interceptors.request.use(
  (config) => {
    const requestHeaders = config.headers || {};
    const currentHeader = requestHeaders.Authorization || requestHeaders.authorization || api.defaults.headers.common.Authorization;
    const token = extractBearerToken(currentHeader);

    if (token) {
      if (isTokenExpired(token)) {
        clearAuthToken();

        if (currentAuthScope) {
          invalidateSession(currentAuthScope, "expired");
        }

        const error = new axios.CanceledError("La sesión expiró.");
        error.code = "SESSION_EXPIRED";
        throw error;
      }

      config.headers = {
        ...requestHeaders,
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestAuthHeader = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
    const requestToken = extractBearerToken(requestAuthHeader);

    if (status === 401 && requestToken && currentAuthScope) {
      clearAuthToken();
      invalidateSession(currentAuthScope, "unauthorized");
    }

    return Promise.reject(error);
  }
);

export const getPublicHeaders = () => (
  PUBLIC_TOKEN ? { authorization: PUBLIC_TOKEN } : {}
);

export const formatApiError = (error, fallbackMessage = "Ocurrió un error inesperado.") => {
  const serverMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.data?.message;

  if (serverMessage) {
    return String(serverMessage);
  }

  if (error?.code === "SESSION_EXPIRED") {
    return "Tu sesión venció. Inicia sesión nuevamente.";
  }

  if (error?.code === "ECONNABORTED") {
    return "La solicitud tardó demasiado. Intenta nuevamente.";
  }

  if (!error?.response) {
    return "No se pudo conectar con el servidor. Verifica tu conexión e intenta otra vez.";
  }

  return fallbackMessage;
};

export const buildMediaUrl = (value) => {
  if (!value) return "";
  const rawValue = String(value).trim();
  if (!rawValue) return "";
  if (/^https?:\/\//i.test(rawValue)) return rawValue;
  return "";
};

export const buildStoredFileUrl = (folder, value) => {
  if (!value) return "";
  const rawValue = String(value).trim();
  if (!rawValue) return "";
  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }
  return "";
};

export const normalizeDriver = (driver = {}) => {
  const taxiPlate = driver.taxiPlate || driver.plate || "";
  const averageRate = Number(driver.averageRate || driver.rating || 0);
  const totalRatings = Number(driver.totalRatings ?? driver.ratingsCount ?? 0);

  return {
    ...driver,
    taxiPlate,
    plate: taxiPlate,
    photo: buildMediaUrl(driver.photo),
    averageRate,
    rating: averageRate,
    totalRatings,
    ratingsCount: totalRatings,
  };
};

export const normalizeJobApplication = (application = {}) => ({
  ...application,
  cvUrl: application.cvUrl || application.cv_url || buildMediaUrl(application.cv),
});

export const normalizeLostItem = (item = {}) => ({
  ...item,
  photo: buildMediaUrl(item.photo),
});

export const normalizeImage = (image = {}) => ({
  ...image,
  url: buildMediaUrl(image.url || image.filename),
});
